import striptags from 'striptags';
import store from '@/store';
import { vueEnv, baseUrl } from '@/globals';

let wps = 200 / 60, // average words per second
  fileTime = 15, // estimated seconds per file
  buttonTime = 60, // estimated time to visit external page
  engagementTime = 30; // estimated seconds for engagement

let getLessonLength = (lesson) => {
  const { items = [] } = lesson;
  let length = 0;

  if (!items || !items.length) {
    return length;
  }

  for (let i of items) {
    length += calcItem(i);
  }
  return length;
};

let calcItem = (item) => {
  let type = item.type,
    vals = item.values,
    length = 0;
  switch (type) {
    case 'video':
    case 'embed':
      if (vals.custom_range && (vals.start || vals.end)) {
        length =
          Number.parseInt(vals.end || 0) - Number.parseInt(vals.start || 0);
        // If length is negative, have invalid end/start
        if (length < 0) {
          console.warn('invalid length for media item', length, item, vals);
          length = 0;
        }
      } else if (vals.summary) {
        length = Math.ceil(vals.summary.length || 0);
      }
      break;
    case 'rich_text':
      let text = striptags(vals.body),
        words = text.split(' ').length;
      length = Math.ceil(words / wps);
      break;
    case 'files':
      let files = vals.files,
        count = files ? files.length : 0;
      length = count * fileTime;
      if (vals.html) {
        let text = striptags(vals.html),
          words = text.split(' ').length;
        length += Math.ceil(words / wps);
      }
      break;
    case 'button':
      length = buttonTime;
      break;
    default:
      // everything else is an engagement or followup
      length = engagementTime;
      break;
  }
  return length;
};

let getLessonBanner = (lesson) => {
  if (lesson.banner_img && !isDefaultBanner(lesson.banner_img)) {
    return lesson.banner_img;
  }

  let items = lesson?.items || [];
  if (!items.length) return;

  let media = findImage(items);
  return media ? media : findThumbnail(items);
};

let findImage = (items) => {
  for (let i of items) {
    if (!i.type) {
      continue;
    }
    if (i.values && i.values.image) {
      return i.values.image;
    }
  }
  return '';
};

let findThumbnail = (items = []) => {
  const banners = items.reduce((acc, i) => {
    if (i.type !== 'video') return acc;
    const banner = extractVideoImage(i);
    if (banner) {
      acc.push(banner);
    }
    return acc;
  }, []);

  if (banners.length > 1) {
    return banners.find((path) => !isDefaultBanner(path));
  }

  return banners[0] || '';
};

const extractVideoImage = (item) => {
  const { summary } = item.values || {};
  return summary?.thumbnail_url || '';
};

const isDefaultBanner = (url) => {
  const DEFAULT_VIDEO_BANNER = 'zlRl8sJU_4I/hqdefault.jpg';
  return url.includes(DEFAULT_VIDEO_BANNER);
};

let getLessonIcon = (lesson, nameOnly) => {
  let items = lesson.items;
  if (!items || !items.length) {
    return;
  }
  return findLessonIcon(lesson.items, nameOnly);
};

let findLessonIcon = (items, nameOnly) => {
  for (let i of items) {
    let def = itemDefinition(i);
    if (def.engagement) {
      return nameOnly ? def.icon : iconPath(def.icon);
    }
  }
  return '';
};

let itemDefinition = (item) => {
  if (!item) {
    return {};
  }
  return store.getters['items/type'](item.type) || {};
};

let iconPath = (icon) => {
  return `lessons/${icon}`;
};

let formatIcon = (item) => {
  let def = itemDefinition(item);
  return iconPath(def.icon);
};

let getStatsFromItems = (items) => {
  let points = 0;
  let maxPoints = 0;
  let completedItems = 0;
  let unfinishedItems = 0;

  items.forEach((item) => {
    let def = itemDefinition(item);

    if (item && item.values.include_points === false) return false;

    let es = item.engagementState || {};
    if (es.points) {
      points += parseInt(es.points);
    }
    if (es.completed) {
      completedItems += 1;
    } else {
      unfinishedItems += 1;
    }

    if (def && def.maxPoints) {
      maxPoints += parseInt(def.maxPoints(item.values) || 0);
    }
  });

  let completedPerc = Math.round((completedItems / items.length) * 100);

  let grade = Math.round((maxPoints === 0 ? 1 : points / maxPoints) * 100);

  //technically you can get a grade greater than 100 with bonus
  //points but it's going to confuse people if we do this.
  grade = grade > 100 ? 100 : grade;

  return {
    points,
    maxPoints,
    grade,
    completedItems,
    unfinishedItems,
    completed: unfinishedItems === 0,
    completedPerc,
  };
};

let setItemValue = (vals, field, value) => {
  if (field.indexOf('.') < 0) {
    vals[field] = value;
    return vals;
  }
  let parts = field.split('.'),
    valObj = vals,
    p = parts.shift();
  while (parts.length) {
    let idx = Number.parseInt(p);
    if (!Number.isNaN(idx)) {
      valObj = valObj[idx];
    } else {
      valObj = valObj[p];
    }
    p = parts.shift();
  }
  valObj[p] = value;
  return vals;
};

export {
  getLessonLength,
  getLessonBanner,
  getLessonIcon,
  formatIcon,
  iconPath,
  getStatsFromItems,
  setItemValue,
};
